import { useMemo, useState } from "react";
import PageHeader from "@/components/base/PageHeader";
import Button from "@/components/base/Button";
import Badge from "@/components/base/Badge";
import Modal from "@/components/base/Modal";
import ConfirmDialog from "@/components/base/ConfirmDialog";
import { Input, Select } from "@/components/base/Field";
import { ErrorState, LoadingState } from "@/components/base/StateViews";
import { useToast } from "@/components/base/Toast";
import { useAsync } from "@/hooks/useAsync";
import { usePager } from "@/hooks/usePager";
import Pagination from "@/components/base/Pagination";
import { addSubject, deleteSubject, getClasses, getSubjects, updateSubject } from "@/services/academicService";
import { getCycles } from "@/services/schoolService";
import type { Cycle, CycleCode, SchoolClass, Subject } from "@/types";

/* ------------------------------------------------------------------------- */
/* Composants locaux                                                          */
/* ------------------------------------------------------------------------- */

interface SubjectRowData {
  cycle: Cycle;
  rows: Subject[];
}

interface SubjectTableProps {
  /** Filtres actifs : retour à la page 1 quand ils changent. */
  filterKey: string;
  data: SubjectRowData;
  cycleLabels: Record<string, string>;
  usage: Record<string, number>;
  onEdit: (subject: Subject) => void;
  onDelete: (subject: Subject) => void;
}

function SubjectTable({ data, filterKey, cycleLabels, usage, onEdit, onDelete }: SubjectTableProps) {
  const page = usePager().slice(data.rows, filterKey);
  const isPrimary = data.cycle.code === "PRIMAIRE";
  const accentTone = isPrimary ? "warning" : "secondary";
  const dot = isPrimary ? "bg-warning-500" : "bg-secondary-500";
  const iconBox = isPrimary
    ? "bg-primary-50 text-primary-700"
    : "bg-accent-50 text-accent-700";

  return (
    <section className="mb-8">
      <div className="mb-3 flex items-center gap-3">
        <span className={`h-2.5 w-2.5 rounded-full ${dot}`}></span>
        <h4 className="text-sm font-semibold font-heading text-foreground-900">
          Cycle {data.cycle.label}
        </h4>
        <Badge tone={accentTone}>{data.rows.length} matières</Badge>
      </div>

      <div className="overflow-hidden rounded-2xl border border-background-200 bg-background-50">
        <div className="overflow-x-auto">
          <table className="w-full min-w-[640px] border-collapse">
            <thead>
              <tr className="border-b border-background-200 bg-background-50/60 text-left text-xs uppercase tracking-wider text-foreground-500">
                <th className="px-5 py-3 font-medium">Matière</th>
                <th className="px-5 py-3 font-medium">Cycle</th>
                <th className="px-5 py-3 font-medium">Classes</th>
                <th className="px-5 py-3 text-right font-medium">Actions</th>
              </tr>
            </thead>
            <tbody>
              {page.items.map((subject) => (
                <tr
                  key={subject.id}
                  className="border-b border-background-200 transition-colors last:border-0 hover:bg-background-100/60"
                >
                  <td className="px-5 py-3.5">
                    <div className="flex items-center gap-3">
                      <span
                        className={`flex h-8 w-8 items-center justify-center rounded-md ${iconBox}`}
                      >
                        <i className="ri-book-open-line"></i>
                      </span>
                      <span className="text-sm font-medium text-foreground-900">
                        {subject.name}
                      </span>
                    </div>
                  </td>
                  <td className="px-5 py-3.5">
                    <Badge tone={accentTone} icon={isPrimary ? "ri-seedling-line" : "ri-book-read-line"}>
                      {cycleLabels[subject.cycleId] ?? "—"}
                    </Badge>
                  </td>
                  <td className="px-5 py-3.5 text-sm text-foreground-700">
                    <span className="font-semibold text-foreground-900">
                      {usage[subject.id] ?? 0}
                    </span>{" "}
                    classes
                  </td>
                  <td className="px-5 py-3.5">
                    <div className="flex items-center justify-end gap-1">
                      <button
                        type="button"
                        onClick={() => onEdit(subject)}
                        aria-label={`Modifier ${subject.name}`}
                        className="flex h-8 w-8 items-center justify-center rounded-md text-foreground-500 transition-colors hover:bg-primary-50 hover:text-primary-700 cursor-pointer"
                      >
                        <i className="ri-pencil-line"></i>
                      </button>
                      <button
                        type="button"
                        onClick={() => onDelete(subject)}
                        aria-label={`Supprimer ${subject.name}`}
                        className="flex h-8 w-8 items-center justify-center rounded-md text-foreground-500 transition-colors hover:bg-accent-50 hover:text-accent-700 cursor-pointer"
                      >
                        <i className="ri-delete-bin-line"></i>
                      </button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
        <Pagination pager={page} className="border-t border-background-100" />
      </div>
    </section>
  );
}

/* ------------------------------------------------------------------------- */
/* Page                                                                       */
/* ------------------------------------------------------------------------- */

interface SubjectFormState {
  cycleId: string;
  name: string;
}

async function loadSubjectsPage() {
  const [cycles, subjects, classes] = await Promise.all([getCycles(), getSubjects(), getClasses()]);
  return { cycles, subjects, classes };
}

export default function SubjectsPage() {
  const { notify } = useToast();
  const { data, loading, error, reload } = useAsync(loadSubjectsPage, []);

  const [search, setSearch] = useState("");
  const [cycleFilter, setCycleFilter] = useState("all");
  const [open, setOpen] = useState(false);
  const [editing, setEditing] = useState<Subject | null>(null);
  const [form, setForm] = useState<SubjectFormState>({ cycleId: "", name: "" });
  const [toDelete, setToDelete] = useState<Subject | null>(null);

  const cycles = useMemo(() => data?.cycles ?? [], [data]);
  const subjects = data?.subjects ?? [];
  const classes = useMemo(() => data?.classes ?? [], [data]);

  const cycleLabels = useMemo(
    () => Object.fromEntries(cycles.map((cycle) => [cycle.id, cycle.label])) as Record<string, string>,
    [cycles],
  );
  const usage = useMemo(() => {
    const map: Record<string, number> = {};
    classes.forEach((cls: SchoolClass) => {
      cls.subjectIds.forEach((subjectId) => {
        map[subjectId] = (map[subjectId] ?? 0) + 1;
      });
    });
    return map;
  }, [classes]);

  if (loading && !data) return <LoadingState label="Chargement des matières..." />;
  if (error || !data) {
    return <ErrorState message={error ?? "Configuration introuvable."} onRetry={reload} />;
  }

  const countByCycleCode = (code: CycleCode) =>
    subjects.filter((subject) => cycles.find((cycle) => cycle.id === subject.cycleId)?.code === code)
      .length;
  const primaryCount = countByCycleCode("PRIMAIRE");
  const secondaryCount = countByCycleCode("SECONDAIRE");

  const term = search.trim().toLowerCase();
  const visibleCycles = cycles.filter((cycle) => cycleFilter === "all" || cycle.id === cycleFilter);
  const filteredRows = visibleCycles.map<SubjectRowData>((cycle) => ({
    cycle,
    rows: subjects.filter((subject) => {
      if (subject.cycleId !== cycle.id) return false;
      if (!term) return true;
      return subject.name.toLowerCase().includes(term);
    }),
  }));
  const tablesToRender = filteredRows.filter((item) => item.rows.length > 0);
  const totalVisible = filteredRows.reduce((sum, item) => sum + item.rows.length, 0);

  const openCreate = () => {
    const defaultCycle = cycleFilter !== "all" ? cycleFilter : cycles[0]?.id ?? "";
    setEditing(null);
    setForm({ cycleId: defaultCycle, name: "" });
    setOpen(true);
  };

  const openEdit = (subject: Subject) => {
    setEditing(subject);
    setForm({ cycleId: subject.cycleId, name: subject.name });
    setOpen(true);
  };

  const handleSave = async () => {
    if (!form.name.trim()) {
      notify("Le nom de la matière est obligatoire.", "error");
      return;
    }
    const name = form.name.trim();

    if (editing) {
      await updateSubject(editing.id, { cycleId: form.cycleId, name });
      notify("Matière mise à jour.");
    } else {
      await addSubject({ cycleId: form.cycleId, name });
      notify(`Matière « ${name} » ajoutée.`);
    }
    await reload();
    setOpen(false);
  };

  return (
    <div className="animate-fade-in">
      <PageHeader
        title="Matières"
        subtitle={`${subjects.length} matières — ${primaryCount} primaire · ${secondaryCount} secondaire`}
        icon="ri-book-open-line"
        actions={
          <Button icon="ri-add-line" onClick={openCreate}>
            Ajouter une matière
          </Button>
        }
      />

      <div className="mb-6 flex flex-col gap-3 sm:flex-row">
        <div className="relative flex-1">
          <span className="pointer-events-none absolute left-3 top-1/2 flex h-4 w-4 -translate-y-1/2 items-center justify-center text-foreground-400">
            <i className="ri-search-line text-sm"></i>
          </span>
          <input
            type="text"
            value={search}
            onChange={(event) => setSearch(event.target.value)}
            placeholder="Rechercher une matière..."
            className="w-full rounded-md border border-background-300 bg-background-50 py-2 pl-9 pr-3 text-sm text-foreground-900 placeholder:text-foreground-400 transition-colors focus:border-primary-400 focus:outline-none focus:ring-2 focus:ring-primary-100"
          />
        </div>
        <div className="relative sm:w-56">
          <span className="pointer-events-none absolute left-3 top-1/2 flex h-4 w-4 -translate-y-1/2 items-center justify-center text-foreground-400">
            <i className="ri-filter-line text-sm"></i>
          </span>
          <select
            value={cycleFilter}
            onChange={(event) => setCycleFilter(event.target.value)}
            className="w-full cursor-pointer appearance-none rounded-md border border-background-300 bg-background-50 py-2 pl-9 pr-9 text-sm text-foreground-800 transition-colors focus:border-primary-400 focus:outline-none focus:ring-2 focus:ring-primary-100"
          >
            <option value="all">Tous les cycles</option>
            {cycles.map((cycle) => (
              <option key={cycle.id} value={cycle.id}>
                {cycle.label}
              </option>
            ))}
          </select>
          <span className="pointer-events-none absolute right-2.5 top-1/2 flex h-4 w-4 -translate-y-1/2 items-center justify-center text-foreground-400">
            <i className="ri-arrow-down-s-line text-base"></i>
          </span>
        </div>
      </div>

      {totalVisible === 0 ? (
        <div className="flex flex-col items-center justify-center gap-3 rounded-2xl border border-dashed border-background-300 bg-background-100/50 py-14 text-center">
          <span className="flex h-12 w-12 items-center justify-center rounded-full bg-background-200 text-foreground-500">
            <i className="ri-book-open-line text-2xl"></i>
          </span>
          <p className="text-sm text-foreground-600">
            Aucune matière ne correspond à votre recherche.
          </p>
        </div>
      ) : (
        tablesToRender.map((item) => (
          <SubjectTable
            key={item.cycle.id}
            data={item}
            filterKey={`${search}|${cycleFilter}`}
            cycleLabels={cycleLabels}
            usage={usage}
            onEdit={openEdit}
            onDelete={setToDelete}
          />
        ))
      )}

      <Modal
        open={open}
        onClose={() => setOpen(false)}
        title={editing ? "Modifier une matière" : "Ajouter une matière"}
        subtitle={
          editing
            ? `Cycle ${cycleLabels[editing.cycleId] ?? "—"}`
            : "La matière sera rattachée au cycle choisi"
        }
        icon="ri-book-open-line"
        size="sm"
        footer={
          <>
            <Button variant="outline" onClick={() => setOpen(false)}>
              Annuler
            </Button>
            <Button icon="ri-save-3-line" onClick={handleSave}>
              Enregistrer
            </Button>
          </>
        }
      >
        <div className="space-y-4">
          <Select
            label="Cycle"
            required
            hint={cycles.length === 0 ? "Aucun cycle : ouvrez-en un dans Paramètres › Cycles." : undefined}
            value={form.cycleId}
            onChange={(event) => setForm((prev) => ({ ...prev, cycleId: event.target.value }))}
          >
            {cycles.map((cycle) => (
              <option key={cycle.id} value={cycle.id}>
                {cycle.label}
              </option>
            ))}
          </Select>
          <Input
            label="Nom de la matière"
            required
            placeholder="ex. Mathématiques"
            value={form.name}
            onChange={(event) => setForm((prev) => ({ ...prev, name: event.target.value }))}
          />
        </div>
      </Modal>

      <ConfirmDialog
        open={toDelete !== null}
        title="Supprimer la matière"
        message={`Voulez-vous vraiment supprimer la matière « ${toDelete?.name ?? ""} » ? Elle sera retirée de toutes les classes concernées.`}
        confirmLabel="Oui, supprimer"
        onClose={() => setToDelete(null)}
        onConfirm={async () => {
          if (!toDelete) return;
          await deleteSubject(toDelete.id);
          await reload();
          notify("Matière supprimée.", "info");
        }}
      />
    </div>
  );
}